/**
 * Pricing helpers shared between the org form and the org mutations.
 *
 * These are extracted into a pure utility so the business rules can be unit
 * tested against the real source instead of inline reimplementations.
 */

/**
 * Resolve the frontend pricing model for an org loaded from the backend.
 *
 * Hybrid is a frontend-only concept. The backend PriceModel enum only knows
 * CPM and Percentage, so we rely on the org config's `isHybrid` flag to know
 * whether a CPM-backed org should actually render as Hybrid. When the flag is
 * absent we fall back to the raw backend model and never infer Hybrid from
 * default AuctionPlus percentage values (which would false-flag every CPM org).
 */
export const resolveModelType = (config, pricingModel) => {
  if (config?.isHybrid === true) return 'Hybrid';
  return pricingModel?.model || 'CPM';
};

/**
 * Map the frontend model type to a value the backend PriceModel enum accepts.
 */
export const mapModelTypeForBackend = (modelType) => {
  return modelType === 'Hybrid' ? 'CPM' : modelType;
};

/**
 * CORE/ENTERPRISE orgs pay the platform directly: their single price IS the
 * contract price, so it is duplicated to both columns (campaign machinery
 * reads advertiserCPM; contract-side consumers read contractCPM).
 *
 * STANDARD/PROSPECTING orgs have no contract with the platform — their single
 * price is the marked-up client rate and only the advertiser column is
 * meaningful. Their contract column is left untouched rather than filled with
 * a misleading placeholder. CHANNEL_PARTNER orgs keep their own separate
 * contract (cost) value.
 */
export const preparePricingField = (level, advertiserValue, contractValue) => {
  const isDirectContract = level === 'CORE' || level === 'ENTERPRISE';
  return isDirectContract ? advertiserValue : contractValue;
};

/**
 * Org levels that hold a contract with the platform: CORE/ENTERPRISE (direct
 * customers) and CHANNEL_PARTNER (the bill payer for their subtree). This is
 * the single source of truth for the level↔contract mapping — used for
 * pricing saves (prepareContractFlag) and contract-only UI like IO numbers.
 */
export const ORG_LEVELS_WITH_CONTRACT = ['CORE', 'ENTERPRISE', 'CHANNEL_PARTNER'];

export const orgLevelHasContract = (level) => ORG_LEVELS_WITH_CONTRACT.includes(level);

/**
 * Whether a campaign's bill payer holds a contract: the org itself when it
 * holds one (CORE/ENTERPRISE/CHANNEL_PARTNER), or any ancestor in its
 * parentOrg chain — orgs billed via a channel partner (the bill payer for
 * its subtree). parentOrg is the full ancestor chain (root first, direct
 * parent last — see buildOrganizationTree in utils/dataManipulating/
 * organization), so every link is checked; PLATFORM roots never match
 * because they are not contract levels.
 */
export const orgTreeHasContract = (org) =>
  orgLevelHasContract(org?.level) ||
  (org?.parentOrg || []).some((ancestor) => orgLevelHasContract(ancestor?.level));

/**
 * Contract status is derived from org level, not a manual toggle:
 * CORE/ENTERPRISE (direct customers) and CHANNEL_PARTNER (the bill payer for
 * their subtree) always have contracts; every other level never does.
 */
export const prepareContractFlag = (level) => {
  return orgLevelHasContract(level);
};
